import type { CaptureResult } from "posthog-js";

import { isBenignRumError } from "./rum-error-filters";

/**
 * PostHog `before_send` predicate: the counterpart to RUM's `shouldSendRumEvent`.
 *
 * PostHog's Error Tracking accumulated the same third-party noise RUM already
 * discards. Sharing `isBenignRumError` keeps the two SDKs from drifting apart:
 * one list, one place to add a pattern.
 *
 * Only ever drops `$exception`: returning `null` too eagerly loses production
 * events with no trace, which is far harder to notice than extra noise. The type
 * check that guarantees that is the first statement, which also keeps the hot
 * path cheap: `$snapshot` fires constantly. The body fails open, because a
 * throw here would take out analytics for the whole page.
 */
export function shouldSendPostHogEvent(
  event: CaptureResult | null,
): CaptureResult | null {
  // Covers null too: `undefined !== "$exception"` returns the null unchanged.
  if (event?.event !== "$exception") {
    return event;
  }

  try {
    // `$exception_list` is the canonical shape; `$exception_message` is the
    // older flat property. Read both so the filter works regardless of which
    // one produced the event.
    const props = event.properties as Record<string, unknown> | undefined;
    const fromList = (
      props?.$exception_list as { value?: unknown }[] | undefined
    )?.[0]?.value;
    const message =
      typeof fromList === "string" ? fromList : props?.$exception_message;

    return isBenignRumError(typeof message === "string" ? message : null)
      ? null
      : event;
  } catch {
    return event;
  }
}
